import type { ReactNode } from 'react';

import { searchJmesPath } from '../../../utils';
import {
  buildSearchJsonLogic,
  stringifyFilters,
} from '../../utils/filterHelpers';

/**
 * A node in the async reference tree. `value` is the instance id (submitted);
 * `termId` is the hierarchy code used to query this node's children. Sentinel
 * "load more" nodes carry `__loadMore` plus the level coordinates needed to
 * fetch the next page.
 */
export interface IAsyncTreeNode {
  value: string;
  title: ReactNode;
  termId?: string;
  isLeaf?: boolean;
  selectable?: boolean;
  checkable?: boolean;
  children?: IAsyncTreeNode[];
  // Sentinel-only fields
  __loadMore?: boolean;
  parentValue?: string | null;
  parentTermId?: string | null;
  page?: number;
}

export interface INodeConfig {
  transformer: string;
  childKey?: string;
  hasChildrenKey?: string;
  selectableExpression?: string;
  leavesOnly: boolean;
  isMultiple: boolean;
}

export interface IOptionMeta {
  modelId?: string;
  version?: number;
  label: string;
  meta: any;
}

/**
 * Split a parent-key JMESPath (e.g. `data.parents[].parent`) into the BE array
 * field and element field used to build the children `some` filter
 * (`parents` / `parent`). The `data.` prefix is dropped because BE filter vars
 * resolve against the instance data.
 */
export const parseParentKey = (
  parentKey?: string
): { arrayField: string; field: string } => {
  if (!parentKey) return { arrayField: '', field: '' };
  const stripped = parentKey.replace(/^data\./, '');
  const [arrayField, field] = stripped.split('[].');
  return { arrayField: arrayField || '', field: field || '' };
};

/**
 * Build the BE filter for one tree level. `parentTermId === null` fetches the
 * roots (via `rootFilter`); otherwise the direct children of that term. The
 * configured list-scope filter is always AND-ed in.
 */
export const buildLevelFilters = ({
  parentTermId,
  scopeFilter,
  rootFilter,
  parentArrayField,
  parentField,
}: {
  parentTermId: string | null;
  scopeFilter?: any;
  rootFilter?: any;
  parentArrayField: string;
  parentField: string;
}): string | undefined => {
  const filters: any[] = [];
  if (scopeFilter) filters.push(scopeFilter);
  if (parentTermId === null) {
    if (rootFilter) filters.push(rootFilter);
  } else {
    filters.push({
      some: [
        { var: parentArrayField },
        { '==': [{ var: parentField }, parentTermId] },
      ],
    });
  }
  return stringifyFilters(filters);
};

export const buildSearchFilters = ({
  search,
  scopeFilter,
  searchableFields,
}: {
  search: string;
  scopeFilter?: any;
  searchableFields?: string[];
}): string | undefined => {
  const filters: any[] = [];
  if (scopeFilter) filters.push(scopeFilter);
  if (searchableFields?.length && search) {
    filters.push(buildSearchJsonLogic(searchableFields, search));
  }
  return stringifyFilters(filters);
};

/**
 * Map a raw model instance to a tree node. The transformer supplies
 * value/label/modelId/version; childKey/hasChildrenKey/selectableExpression are
 * read from the raw instance. `leavesOnly` makes parent nodes expand-only.
 */
export const mapInstanceToNode = (
  instance: any,
  cfg: INodeConfig
): { node: IAsyncTreeNode; meta: IOptionMeta } => {
  const itemTransformer = cfg.transformer.replace('[].', '');
  const t = searchJmesPath(instance, itemTransformer) || {};

  const value = String(t.value ?? instance.id);
  const label = t.label ?? instance.id;
  const termId = cfg.childKey
    ? String(searchJmesPath(instance, cfg.childKey) ?? '')
    : '';
  const hasChildren = cfg.hasChildrenKey
    ? !!searchJmesPath(instance, cfg.hasChildrenKey)
    : false;
  const baseSelectable = cfg.selectableExpression
    ? !!searchJmesPath(instance, cfg.selectableExpression)
    : true;
  const selectable = baseSelectable && (!cfg.leavesOnly || !hasChildren);

  const node: IAsyncTreeNode = {
    value,
    title: label,
    termId,
    isLeaf: !hasChildren,
    selectable,
    // In checkable (multiple) mode, hide the checkbox on non-selectable nodes
    // so they stay expand-only. Ignored in single mode.
    checkable: selectable,
  };

  return {
    node,
    meta: { modelId: t.modelId, version: t.version, label, meta: t },
  };
};

/**
 * Recursively replace the children of the node matching `targetValue`.
 * Returns a new tree (immutable update).
 */
export const setNodeChildren = (
  nodes: IAsyncTreeNode[],
  targetValue: string,
  children: IAsyncTreeNode[]
): IAsyncTreeNode[] =>
  nodes.map(n => {
    if (n.value === targetValue) return { ...n, children };
    if (n.children) {
      return {
        ...n,
        children: setNodeChildren(n.children, targetValue, children),
      };
    }
    return n;
  });

/**
 * Append freshly-fetched siblings to a level, dropping the old "load more"
 * sentinel and re-adding one only when there is a further page. `parentValue`
 * null targets the root level.
 */
export const appendToLevel = (
  nodes: IAsyncTreeNode[],
  parentValue: string | null,
  makeSentinel: () => IAsyncTreeNode,
  newNodes: IAsyncTreeNode[],
  hasNext: boolean
): IAsyncTreeNode[] => {
  if (parentValue === null) {
    return withSentinel(nodes, makeSentinel, newNodes, hasNext);
  }
  return nodes.map(n => {
    if (n.value === parentValue) {
      return {
        ...n,
        children: withSentinel(
          n.children ?? [],
          makeSentinel,
          newNodes,
          hasNext
        ),
      };
    }
    if (n.children) {
      return {
        ...n,
        children: appendToLevel(
          n.children,
          parentValue,
          makeSentinel,
          newNodes,
          hasNext
        ),
      };
    }
    return n;
  });
};

const withSentinel = (
  existing: IAsyncTreeNode[],
  makeSentinel: () => IAsyncTreeNode,
  newNodes: IAsyncTreeNode[],
  hasNext: boolean
): IAsyncTreeNode[] => {
  const withoutSentinel = existing.filter(n => !n.__loadMore);
  const result = [...withoutSentinel, ...newNodes];
  if (hasNext) result.push(makeSentinel());
  return result;
};
